import { useNavigate, useSearchParams } from 'react-router-dom';
import { CardWrapper } from '../common/wrapper/card/wrapper';
import { ContentWrapper } from '../common/wrapper/content/wrapper';
import { Routes } from '../common/router/constants';
import { useAuthContext } from 'src/context/auth.context';
import {
  BottomNavigation,
  Button,
  Controller,
  Input,
  Select,
  Switch,
  TextEditor,
  Typography,
  useForm,
} from '@otp/ui';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import {
  UpdateUserDataDocument,
  UpdateUserProfileDocument,
} from 'src/graphql/generated/graphql';
import { queryStringToObject } from 'src/utils/query-string';
import { useUser } from 'src/hooks/query/use-user';

type Form = {
  mobilePhone: string;
  email: string;
  communicationEmail: string;
  preferredEmailLanguage: string;
  isReceivingTextMessages: boolean;
  aboutMe: string;
};

export const EditUserProfile = (editUser: { id?: string }) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'user-profile.details',
  });
  const navigate = useNavigate();
  const { user: authUser, loadUser } = useAuthContext();
  const [searchParams] = useSearchParams();
  const urlParams = queryStringToObject(searchParams.toString());
  const userId = editUser?.id;

  const { user: loadedUser } = useUser({ id: userId as string, skip: !userId });
  const user = userId ? loadedUser : authUser;

  const [updateCurrentUserProfile, { loading }] = useMutation(
    UpdateUserProfileDocument,
  );

  const [updateUser] = useMutation(UpdateUserDataDocument);

  const onSubmit = async (formData: Form) => {
    const { email: _, ...restFormData } = formData;

    if (userId) {
      await updateUser({
        variables: {
          id: userId,
          data: restFormData,
        },
      });
    } else {
      await updateCurrentUserProfile({
        variables: {
          data: restFormData,
        },
      });
    }

    await loadUser();

    onClose();
  };

  const onClose = () => {
    if (urlParams.postRedirect) {
      Routes[urlParams.postRedirect] &&
        navigate(`${Routes[urlParams.postRedirect].path}`);
    } else if (userId) {
      navigate(`${Routes.users.path}/${userId}`);
    } else {
      navigate(Routes.profile.path);
    }
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Form>({
    defaultValues: {
      mobilePhone: user?.mobilePhone || '',
      email: user?.email || '',
      communicationEmail: user?.communicationEmail || '',
      preferredEmailLanguage: user?.preferredEmailLanguage || '',
      isReceivingTextMessages: user?.isReceivingTextMessages || false,
      aboutMe: user?.aboutMe || '',
    },
  });

  return (
    <ContentWrapper>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardWrapper>
          <div className="mb-4 max-w-lg">
            <Controller
              name="mobilePhone"
              control={control}
              rules={{
                validate: (value) => {
                  const phoneRegex = /^\+?[\d\s]{9,20}$/;
                  return phoneRegex.test(value) || false;
                },
              }}
              render={({ field }) => (
                <Input
                  label={t('mobile-phone')}
                  placeholder={t('mobile-phone')}
                  error={!!errors.mobilePhone}
                  {...field}
                  defaultValue={field.value}
                />
              )}
            />
          </div>
          <div className="mb-4">
            <Controller
              name="isReceivingTextMessages"
              control={control}
              render={({ field }) => (
                <div className="flex">
                  <Typography variant="subtitle1" className="mt-2.5">
                    {t('is-receiving-text-messages')}
                  </Typography>
                  <Switch
                    checked={field.value}
                    onChange={(_event, checked) => {
                      field.onChange(checked);
                    }}
                  />
                </div>
              )}
            />
          </div>
          <div className="mb-4 max-w-lg">
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <Input
                  label={t('email')}
                  placeholder={t('email')}
                  error={!!errors.email}
                  {...field}
                  defaultValue={field.value}
                  type="email"
                  disabled
                />
              )}
            />
          </div>
          <div className="mb-4 max-w-lg">
            <Controller
              name="communicationEmail"
              control={control}
              render={({ field }) => (
                <Input
                  label={t('communication-email')}
                  placeholder={t('communication-email')}
                  error={!!errors.communicationEmail}
                  {...field}
                  defaultValue={field.value}
                  type="email"
                />
              )}
            />
          </div>
          <div className="mb-4 max-w-lg">
            <Controller
              name="preferredEmailLanguage"
              control={control}
              render={({ field }) => (
                <div>
                  <Typography className="mb-2" variant="subtitle1">
                    {t('preferred-email-language')}
                  </Typography>
                  <Select
                    className="w-full"
                    placeholder={t('preferred-email-language')}
                    error={!!errors.preferredEmailLanguage}
                    {...field}
                    defaultValue={field.value}
                    options={[
                      { value: 'en-US', name: t('lang.english') },
                      { value: 'es-ES', name: t('lang.spanish') },
                      { value: 'fr-FR', name: t('lang.french') },
                      { value: 'de-DE', name: t('lang.german') },
                    ]}
                  />
                </div>
              )}
            />
          </div>
          <div className="mb-4 max-w-lg">
            <Controller
              name="aboutMe"
              control={control}
              render={({ field }) => (
                <div>
                  <Typography className="mb-2" variant="subtitle1">
                    {t('about-me')}
                  </Typography>
                  <TextEditor {...field} defaultValue={field.value} />
                </div>
              )}
            />
          </div>
        </CardWrapper>
        <BottomNavigation>
          <Button
            className="mr-2"
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{ minWidth: 128 }}
          >
            {t('buttons.save')}
          </Button>
          <Button className="mr-4" variant="outlined" onClick={onClose}>
            {t('buttons.cancel')}
          </Button>
        </BottomNavigation>
      </form>
    </ContentWrapper>
  );
};
